import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('michca_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 — redirect to login
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('michca_token');
      localStorage.removeItem('michca_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  invite: (data) => api.post('/auth/invite', data),
  acceptInvite: (token, password) => api.post('/auth/accept-invite', { token, password }),
  changePassword: (current, next) => api.post('/auth/change-password', { current_password: current, new_password: next }),
};

// ─── Series ───────────────────────────────────────────────────────────────────
export const seriesAPI = {
  list: () => api.get('/series'),
  get: (id) => api.get(`/series/${id}`),
  create: (data) => api.post('/series', data),
  update: (id, data) => api.put(`/series/${id}`, data),
  delete: (id) => api.delete(`/series/${id}`),
  getGeneratorConfig: (id) => api.get(`/series/${id}/generator-config`),
  saveGeneratorConfig: (id, cfg) => api.put(`/series/${id}/generator-config`, cfg),
};

// ─── Divisions ────────────────────────────────────────────────────────────────
export const divisionsAPI = {
  list: (series_id) => api.get('/divisions', { params: { series_id } }),
  get: (id) => api.get(`/divisions/${id}`),
  create: (data) => api.post('/divisions', data),
  update: (id, data) => api.put(`/divisions/${id}`, data),
  delete: (id) => api.delete(`/divisions/${id}`),
};

// ─── Teams ────────────────────────────────────────────────────────────────────
export const teamsAPI = {
  list: (params) => api.get('/teams', { params }),
  get: (id) => api.get(`/teams/${id}`),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
  addUmpire: (teamId, data) => api.post(`/teams/${teamId}/umpires`, data),
  removeUmpire: (teamId, uid) => api.delete(`/teams/${teamId}/umpires/${uid}`),
  addComment: (teamId, comment) => api.post(`/teams/${teamId}/comments`, { comment }),
  addWarning: (teamId, data) => api.post(`/teams/${teamId}/warnings`, data),
  importExcel: (series_id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/teams/import/excel?series_id=${series_id}`, file, {
      headers: { 'Content-Type': 'application/octet-stream' }
    });
  },
  templateUrl: () => `${API_URL}/teams/import/template?token=${localStorage.getItem('michca_token')}`,
};

// ─── Grounds ──────────────────────────────────────────────────────────────────
export const groundsAPI = {
  list: () => api.get('/grounds'),
  get: (id) => api.get(`/grounds/${id}`),
  create: (data) => api.post('/grounds', data),
  update: (id, data) => api.put(`/grounds/${id}`, data),
  delete: (id) => api.delete(`/grounds/${id}`),
  getAvailability: (groundId, params) => api.get(`/grounds/${groundId}/availability`, { params }),
  getAllAvailability: (series_id) => api.get(`/grounds/availability/series/${series_id}`),
  saveAvailability: (groundId, records) => {
    // records = [{ available_date, status, series_id, division_id }]
    // group by series_id+division_id and send in backend format
    if (!records || records.length === 0) return Promise.resolve({ data: {} });
    const { series_id, division_id } = records[0];
    const dates = records.map(r => ({ date: r.available_date, status: r.status }));
    return api.post(`/grounds/${groundId}/availability`, { series_id, division_id: division_id || null, dates });
  },
  getBlackouts: (series_id) => api.get('/grounds/blackouts', { params: { series_id } }),
  addBlackout: (series_id, data) => api.post('/grounds/blackouts', { ...data, series_id }),
  deleteBlackout: (id) => api.delete(`/grounds/blackouts/${id}`),
};

// ─── Conditions ───────────────────────────────────────────────────────────────
export const conditionsAPI = {
  list: (params) => api.get('/conditions', { params }),
  create: (data) => api.post('/conditions', data),
  update: (id, data) => api.put(`/conditions/${id}`, data),
  delete: (id) => api.delete(`/conditions/${id}`),
};

// ─── Matchup Matrix ───────────────────────────────────────────────────────────
export const matrixAPI = {
  get: (divisionId) => api.get(`/matrix/${divisionId}`),
  save: (divisionId, cells) => api.post(`/matrix/${divisionId}`, { cells }),
  import: (divisionId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/matrix/${divisionId}/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  exportUrl: (divisionId) => `${API_URL}/matrix/${divisionId}/export`,
};

// ─── Fixtures / Schedule ──────────────────────────────────────────────────────
export const fixturesAPI = {
  list: (params) => api.get('/fixtures', { params }),
  stats: (series_id) => api.get('/fixtures/stats', { params: { series_id } }),
  create: (data) => api.post('/fixtures', data),
  update: (id, data) => api.put(`/fixtures/${id}`, data),
  deleteSeries: (series_id) => api.delete(`/fixtures/series/${series_id}`),
  generate: (seriesId, data) => api.post(`/fixtures/generate/${seriesId}`, data),
  autoResolveConflicts: (series_id, opts = {}) =>
    api.post(`/fixtures/${series_id}/auto-resolve-conflicts`, {
      dry_run: opts.dryRun !== false,
      max_hops: opts.maxHops ?? 1,
      division_ids: opts.divisionIds || null,
      per_team_move_cap: opts.perTeamMoveCap ?? null,
      max_gap_days: opts.maxGapDays ?? null,
      try_multiple_orderings: opts.tryMultipleOrderings === true,
      exhaustive: opts.exhaustive === true,
      time_budget_ms: opts.timeBudgetMs ?? null,
      plan: opts.plan || null,
    }),
  listSnapshots: (series_id) => api.get(`/fixtures/${series_id}/snapshots`),
  restoreSnapshot: (series_id, snapshot_id) => api.post(`/fixtures/${series_id}/snapshots/${snapshot_id}/restore`),
  exportExcelUrl: (series_id) => `${API_URL}/fixtures/export/excel?series_id=${series_id}&token=${localStorage.getItem('michca_token')}`,
  exportPlayoffUrl: (series_id, division_id) => `${API_URL}/playoffs/export/excel?series_id=${series_id}${division_id ? `&division_id=${division_id}` : ''}&token=${localStorage.getItem('michca_token')}`,
  standingsTemplateUrl: () => `${API_URL}/standings/template?token=${localStorage.getItem('michca_token')}`,
  importStandingsExcel: (data) => api.post('/standings/import-excel', data),
};

// ─── CricClubs ────────────────────────────────────────────────────────────────
export const cricclubsAPI = {
  status: () => api.get('/cricclubs/status'),
  test: (method) => api.post('/cricclubs/test', { method }),
  pull: (data) => api.post('/cricclubs/pull', data),
  push: (local_series_id) => api.post('/cricclubs/push', { local_series_id }),
  listCricclubsSeries: (method) => api.get('/cricclubs/series', { params: { method } }),
};

// ─── Users ────────────────────────────────────────────────────────────────────
export const usersAPI = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  approve: (id) => api.put(`/users/${id}/approve`),
  delete: (id) => api.delete(`/users/${id}`),
  listRoles: () => api.get('/users/roles'),
  createRole: (data) => api.post('/users/roles', data),
};
